import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import LoadingSpinner from '../components/blocks/LoadingSpinner';
import ShadedSelect from '../components/ui/ShadedSelect';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const TIME_RANGE_VALUES = [7, 30, 90, 365, 'all'];
const SUBJECT_CODES = [
  'CHI', 'ENG', 'MAT', 'CSD', 'BIO', 'CHE', 'PHY', 'CHL', 'LIE', 'CHH', 'ECO', 'ERS', 'GEO', 'HIS', 'THS',
  'BAF', 'ACC', 'BUS', 'DAT', 'HSC', 'ICT', 'FST', 'FCT', 'MUS', 'VSA', 'PHE', 'ME1', 'ME2'
];

const CHART_COLORS = [
  'rgb(41, 73, 106)',
  'rgb(59, 130, 246)',
  'rgb(16, 185, 129)',
  'rgb(245, 158, 11)',
  'rgb(239, 68, 68)',
  'rgb(139, 92, 246)',
  'rgb(20, 184, 166)',
  'rgb(249, 115, 22)',
  'rgb(100, 116, 139)',
  'rgb(14, 165, 233)'
];

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function getDateString(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getTimeRangeData(raw, days) {
  const today = new Date();
  let startDate;
  if (days === 'all') {
    const allDates = Object.keys(raw || {}).sort();
    if (allDates.length === 0) return { labels: [], data: [] };
    startDate = new Date(allDates[0]);
  } else {
    startDate = new Date(today);
    startDate.setDate(today.getDate() - Number(days) + 1);
  }
  const labels = [];
  const data = [];
  for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
    const key = getDateString(d);
    labels.push(key);
    data.push(raw[key] || 0);
  }
  return { labels, data };
}

function rgbaFromRgb(rgb, alpha) {
  return rgb.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
}

function shortenRouteLabel(label) {
  if (!label) return '';
  if (label.length <= 42) return label;
  return `${label.slice(0, 20)}…${label.slice(-18)}`;
}

function readThemeColors() {
  if (typeof window === 'undefined') {
    return {
      muted: 'rgb(100, 116, 139)',
      grid: 'rgba(229, 231, 235, 0.7)',
      tooltipBg: 'rgb(17, 24, 39)'
    };
  }
  const dark = document.documentElement.classList.contains('dark');
  return dark
    ? {
        muted: 'rgb(161, 161, 170)',
        grid: 'rgba(63, 63, 70, 0.7)',
        tooltipBg: 'rgb(24, 24, 27)'
      }
    : {
        muted: 'rgb(100, 116, 139)',
        grid: 'rgba(229, 231, 235, 0.7)',
        tooltipBg: 'rgb(17, 24, 39)'
      };
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-lg border border-edge bg-surface-muted px-3 py-2.5">
      <p className="text-xs font-medium text-ink-muted">{label}</p>
      <p className="mt-0.5 text-lg font-semibold text-ink tabular-nums">{value}</p>
    </div>
  );
}

function SectionCard({ title, hint, actions, children }) {
  return (
    <section className="rounded-lg border border-edge bg-surface p-4 sm:p-5 overflow-hidden">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {hint ? <p className="mt-0.5 text-xs text-ink-muted">{hint}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div> : null}
      </div>
      <div className="mt-4 min-w-0">{children}</div>
    </section>
  );
}

function ChartLegend({ datasets }) {
  if (!datasets?.length) return null;
  return (
    <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5">
      {datasets.map((ds) => (
        <li key={ds.label} className="flex min-w-0 max-w-full items-center gap-1.5 text-xs text-ink-muted">
          <span
            className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: ds.borderColor }}
            aria-hidden
          />
          <span className="truncate" title={ds.label}>
            {shortenRouteLabel(ds.label)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ChartPanel({ data, yLabel, t }) {
  const theme = readThemeColors();
  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 8, right: 12, bottom: 4, left: 4 } },
      plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: {
          backgroundColor: theme.tooltipBg,
          titleFont: { size: 12 },
          bodyFont: { size: 11 },
          callbacks: {
            label(ctx) {
              const label = ctx.dataset?.label || '';
              const value = ctx.parsed?.y ?? ctx.raw;
              return `${shortenRouteLabel(label)}: ${value}`;
            }
          }
        }
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 0,
            minRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
            color: theme.muted,
            font: { size: 10 }
          },
          grid: { color: theme.grid, drawBorder: false }
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: yLabel || t('analytics.count'),
            color: theme.muted,
            font: { size: 11 }
          },
          ticks: { color: theme.muted, font: { size: 10 }, precision: 0 },
          grid: { color: theme.grid, drawBorder: false }
        }
      }
    }),
    [theme.grid, theme.muted, theme.tooltipBg, yLabel, t]
  );

  return (
    <div className="min-w-0">
      <div className="relative h-64 w-full overflow-hidden sm:h-72">
        <Line data={data} options={options} />
      </div>
      <ChartLegend datasets={data.datasets} />
    </div>
  );
}

function buildRouteDatasets({
  labels,
  byRoute,
  topX,
  totalLabel,
  otherLabel,
  totalColor = 'rgb(160, 196, 255)'
}) {
  const allRoutes = Object.keys(byRoute || {});
  const sortedRoutes = [...allRoutes].sort((a, b) => {
    const sumA = Object.values(byRoute[a] || {}).reduce((acc, v) => acc + v, 0);
    const sumB = Object.values(byRoute[b] || {}).reduce((acc, v) => acc + v, 0);
    return sumB - sumA;
  });
  const mainCount = topX === 'all' ? sortedRoutes.length : Number(topX);
  const mainRoutes = sortedRoutes.slice(0, mainCount);
  const otherRoutes = sortedRoutes.slice(mainCount);

  const datasets = mainRoutes.map((route, i) => {
    const color = CHART_COLORS[i % CHART_COLORS.length];
    return {
      label: route,
      data: labels.map((day) => byRoute[route]?.[day] || 0),
      borderColor: color,
      backgroundColor: rgbaFromRgb(color, 0.15),
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.2,
      fill: false
    };
  });

  if (otherRoutes.length > 0) {
    datasets.push({
      label: otherLabel,
      data: labels.map((day) =>
        otherRoutes.reduce((sum, route) => sum + (byRoute[route]?.[day] || 0), 0)
      ),
      borderColor: 'rgb(148, 163, 184)',
      backgroundColor: 'rgba(148, 163, 184, 0.15)',
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.2,
      fill: false
    });
  }

  datasets.unshift({
    label: totalLabel,
    data: labels.map((day) =>
      allRoutes.reduce((sum, route) => sum + (byRoute[route]?.[day] || 0), 0)
    ),
    borderColor: totalColor,
    backgroundColor: rgbaFromRgb(totalColor, 0.12),
    borderWidth: 2.5,
    pointRadius: 0,
    tension: 0.2,
    fill: false,
    order: 0
  });

  return datasets;
}

const PresentationAnalytics = () => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();

  const getUserText = (count) => (count === 1 ? t('analytics.user') : t('analytics.users'));

  const topXOptions = useMemo(
    () => [
      { label: t('analytics.topX4'), value: 4 },
      { label: t('analytics.topX8'), value: 8 },
      { label: t('analytics.topX16'), value: 16 },
      { label: t('analytics.topAll'), value: 'all' }
    ],
    [t]
  );

  const timeRangeOptions = useMemo(
    () =>
      TIME_RANGE_VALUES.map((value) => ({
        value,
        label: t(`analytics.timeRange.${value}`)
      })),
    [t]
  );

  const [dseScoreDistribution, setDseScoreDistribution] = useState({});
  const [userCreationRaw, setUserCreationRaw] = useState({});
  const [usageDateRaw, setUsageDateRaw] = useState({});
  const [uniqueUsersDateRaw, setUniqueUsersDateRaw] = useState({});
  const [usageByDayByRoute, setUsageByDayByRoute] = useState({});
  const [uniqueUsersByDayByRoute, setUniqueUsersByDayByRoute] = useState({});
  const [routeTotalRequests, setRouteTotalRequests] = useState([]);
  const [enhancedAnalytics, setEnhancedAnalytics] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [userTimeRange, setUserTimeRange] = useState(30);
  const [usageTimeRange, setUsageTimeRange] = useState(30);
  const [uniqueUsersTimeRange, setUniqueUsersTimeRange] = useState(30);
  const [topXRoutes, setTopXRoutes] = useState(8);
  const [dseDisplayMode, setDseDisplayMode] = useState('subjectOrder');
  const [showEnhancedAnalytics, setShowEnhancedAnalytics] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchAnalytics = async () => {
      setLoading(true);
      setError('');
      try {
        const headers = authHeaders();
        const [userStatsRes, dailyAnalyticsRes] = await Promise.all([
          fetch('/api/analytics/user-stats', { headers }),
          fetch('/api/usage/daily-analytics', { headers })
        ]);

        if (!userStatsRes.ok) throw new Error('Failed to fetch user stats');
        if (!dailyAnalyticsRes.ok) throw new Error('Failed to fetch daily analytics');

        const [userStats, dailyAnalytics] = await Promise.all([
          userStatsRes.json(),
          dailyAnalyticsRes.json()
        ]);

        if (cancelled) return;

        setDseScoreDistribution(userStats.dseScoreDistribution || {});
        setUserCreationRaw(userStats.signupsByDay || {});
        setUserCount(Number(userStats.userCount) || 0);

        const processedUsageByDay = {};
        const processedUsageByDayByRoute = {};
        const processedUniqueUsersByDay = {};
        const processedUniqueUsersByDayByRoute = {};

        (dailyAnalytics.data || []).forEach((routeData) => {
          const routeKey = `${routeData.route_method} ${routeData.route_path}`;
          processedUsageByDayByRoute[routeKey] = {};
          processedUniqueUsersByDayByRoute[routeKey] = {};

          (routeData.daily_tallies || []).forEach((tally) => {
            processedUsageByDay[tally.date] =
              (processedUsageByDay[tally.date] || 0) + tally.total_requests;
            processedUniqueUsersByDay[tally.date] =
              (processedUniqueUsersByDay[tally.date] || 0) + tally.unique_users;
            processedUsageByDayByRoute[routeKey][tally.date] = tally.total_requests;
            processedUniqueUsersByDayByRoute[routeKey][tally.date] = tally.unique_users;
          });
        });

        setUsageDateRaw(processedUsageByDay);
        setUniqueUsersDateRaw(processedUniqueUsersByDay);
        setUsageByDayByRoute(processedUsageByDayByRoute);
        setUniqueUsersByDayByRoute(processedUniqueUsersByDayByRoute);
        setRouteTotalRequests(
          (dailyAnalytics.data || []).map((row) => ({
            route_path: row.route_path,
            route_method: row.route_method,
            total_requests: row.total_requests
          }))
        );
        setEnhancedAnalytics(dailyAnalytics.data || []);
      } catch (err) {
        if (!cancelled) {
          setError(t('analytics.errorLoadingData'));
          console.error('[analytics] fetch failed:', err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAnalytics();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const userCreationData = useMemo(
    () => getTimeRangeData(userCreationRaw, userTimeRange),
    [userCreationRaw, userTimeRange]
  );
  const usageDateData = useMemo(
    () => getTimeRangeData(usageDateRaw, usageTimeRange),
    [usageDateRaw, usageTimeRange]
  );
  const uniqueUsersDateData = useMemo(
    () => getTimeRangeData(uniqueUsersDateRaw, uniqueUsersTimeRange),
    [uniqueUsersDateRaw, uniqueUsersTimeRange]
  );

  const totalRouteRequests = useMemo(
    () => routeTotalRequests.reduce((sum, row) => sum + (Number(row.total_requests) || 0), 0),
    [routeTotalRequests]
  );

  const signupTotal = useMemo(
    () => Object.values(userCreationRaw).reduce((sum, n) => sum + (Number(n) || 0), 0),
    [userCreationRaw]
  );

  const translateCSDScoreForDisplay = useCallback(
    (score) => {
      if (!score) return score;
      if (score === t('dse.attained') || score === t('dse.failed')) return score;
      let englishScore = score;
      if (score === '達標' || score === '达标') englishScore = 'Attained';
      else if (score === '不達標' || score === '不达标') englishScore = 'Failed';
      if (englishScore === 'Attained') return t('dse.attained');
      if (englishScore === 'Failed') return t('dse.failed');
      return score;
    },
    [t]
  );

  const mappedDseScoreDistribution = useMemo(() => {
    const mappedDistribution = {};
    Object.entries(dseScoreDistribution || {}).forEach(([subjectKey, scoreValue]) => {
      let subjectCode = subjectKey;
      switch (subjectKey) {
        case 'Chinese Language':
          subjectCode = 'CHI';
          break;
        case 'English Language':
          subjectCode = 'ENG';
          break;
        case 'Mathematics Compulsory Part':
          subjectCode = 'MAT';
          break;
        case 'Core Subject Code':
        case 'Core Score':
          return;
        default:
          subjectCode = subjectKey;
      }

      if (!mappedDistribution[subjectCode]) mappedDistribution[subjectCode] = {};

      if (typeof scoreValue === 'object' && scoreValue !== null) {
        Object.entries(scoreValue).forEach(([score, count]) => {
          const translatedScore =
            subjectCode === 'CSD' ? translateCSDScoreForDisplay(score) : score;
          mappedDistribution[subjectCode][translatedScore] =
            (mappedDistribution[subjectCode][translatedScore] || 0) + count;
        });
      } else if (typeof scoreValue === 'string' && scoreValue) {
        const translatedScore =
          subjectCode === 'CSD' ? translateCSDScoreForDisplay(scoreValue) : scoreValue;
        mappedDistribution[subjectCode][translatedScore] =
          (mappedDistribution[subjectCode][translatedScore] || 0) + 1;
      }
    });
    return mappedDistribution;
  }, [dseScoreDistribution, translateCSDScoreForDisplay]);

  const scoreColumns = useMemo(
    () => ['5**', '5*', '5', '4', '3', '2', '1', t('dse.attained'), t('dse.failed')],
    [t]
  );

  const dseSubjectRows = useMemo(() => {
    if (dseDisplayMode === 'totalCount') {
      return Object.keys(mappedDseScoreDistribution)
        .filter((code) => SUBJECT_CODES.includes(code))
        .sort((a, b) => {
          const totalA = Object.values(mappedDseScoreDistribution[a] || {}).reduce((s, v) => s + v, 0);
          const totalB = Object.values(mappedDseScoreDistribution[b] || {}).reduce((s, v) => s + v, 0);
          return totalB - totalA;
        });
    }
    return SUBJECT_CODES;
  }, [dseDisplayMode, mappedDseScoreDistribution]);

  const sortedRouteTotals = useMemo(
    () => [...routeTotalRequests].sort((a, b) => b.total_requests - a.total_requests),
    [routeTotalRequests]
  );

  if (!currentUser) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="large" text={t('analytics.loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-8 sm:px-6 lg:px-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ink">{t('analytics.analyticsDashboard')}</h1>
        <p className="mt-1 text-sm text-ink-muted">{t('analytics.dashboardHint')}</p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <StatCard label={t('analytics.statUsers')} value={userCount.toLocaleString()} />
        <StatCard label={t('analytics.statSignups')} value={signupTotal.toLocaleString()} />
        <StatCard label={t('analytics.statRoutes')} value={routeTotalRequests.length.toLocaleString()} />
        <StatCard
          label={t('analytics.statTotalRequests')}
          value={totalRouteRequests.toLocaleString()}
        />
      </div>

      <SectionCard
        title={t('analytics.dseScoreDistribution')}
        hint={t('analytics.dseHint')}
        actions={
          <ShadedSelect
            className="min-w-[11rem]"
            value={dseDisplayMode}
            onChange={setDseDisplayMode}
            aria-label={t('analytics.displayMode')}
            size="sm"
            options={[
              { value: 'subjectOrder', label: t('analytics.bySubjectOrder') },
              { value: 'totalCount', label: t('analytics.byTotalCount') }
            ]}
          />
        }
      >
        <div className="overflow-x-auto rounded-md border border-edge">
          <table className="w-full min-w-[40rem] text-sm">
            <thead className="bg-surface-muted">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-ink-muted">
                  {t('analytics.subject')}
                </th>
                {scoreColumns.map((score) => (
                  <th
                    key={score}
                    className="px-2 py-2 text-right text-xs font-semibold text-ink-muted whitespace-nowrap"
                  >
                    {score}
                  </th>
                ))}
                <th className="px-3 py-2 text-right text-xs font-semibold text-ink-muted">
                  {t('common.total')}
                </th>
              </tr>
            </thead>
            <tbody>
              {dseSubjectRows.map((code) => {
                const total = scoreColumns.reduce(
                  (sum, score) => sum + (mappedDseScoreDistribution[code]?.[score] || 0),
                  0
                );
                if (dseDisplayMode === 'subjectOrder' && total === 0) return null;
                return (
                  <tr key={code} className="border-t border-edge">
                    <td className="px-3 py-2 text-left font-medium text-ink">
                      {t(`dseSubjects.${code}`)}
                    </td>
                    {scoreColumns.map((score) => (
                      <td key={score} className="px-2 py-2 text-right tabular-nums text-ink-muted">
                        {mappedDseScoreDistribution[code]?.[score] || 0}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-ink">
                      {total}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard
        title={t('analytics.userSignupsOverTime')}
        actions={
          <ShadedSelect
            className="min-w-[9rem]"
            value={userTimeRange}
            aria-label={t('analytics.selectTimeRange')}
            size="sm"
            onChange={(v) => setUserTimeRange(v === 'all' ? 'all' : Number(v))}
            options={timeRangeOptions}
          />
        }
      >
        {userCreationData.labels.length ? (
          <ChartPanel
            t={t}
            yLabel={t('analytics.count')}
            data={{
              labels: userCreationData.labels,
              datasets: [
                {
                  label: t('analytics.userSignups'),
                  data: userCreationData.data,
                  borderColor: 'rgb(160, 196, 255)',
                  backgroundColor: 'rgba(160, 196, 255, 0.15)',
                  borderWidth: 2,
                  pointRadius: 0,
                  tension: 0.25,
                  fill: true
                }
              ]
            }}
          />
        ) : (
          <p className="text-sm text-ink-muted">{t('analytics.emptyChart')}</p>
        )}
      </SectionCard>

      <SectionCard
        title={t('analytics.routeCallsOverTime')}
        actions={
          <>
            <ShadedSelect
              className="min-w-[9rem]"
              value={usageTimeRange}
              aria-label={t('analytics.selectTimeRange')}
              size="sm"
              onChange={(v) => setUsageTimeRange(v === 'all' ? 'all' : Number(v))}
              options={timeRangeOptions}
            />
            <ShadedSelect
              className="min-w-[8rem]"
              value={topXRoutes}
              size="sm"
              onChange={(v) => setTopXRoutes(v === 'all' ? 'all' : parseInt(v, 10))}
              options={topXOptions}
            />
          </>
        }
      >
        {usageDateData.labels.length ? (
          <ChartPanel
            t={t}
            yLabel={t('analytics.count')}
            data={{
              labels: usageDateData.labels,
              datasets: buildRouteDatasets({
                labels: usageDateData.labels,
                byRoute: usageByDayByRoute,
                topX: topXRoutes,
                totalLabel: t('analytics.routeCalls'),
                otherLabel: t('analytics.endpointOther')
              })
            }}
          />
        ) : (
          <p className="text-sm text-ink-muted">{t('analytics.emptyChart')}</p>
        )}
      </SectionCard>

      <SectionCard
        title={t('analytics.uniqueUsersOverTime')}
        actions={
          <>
            <ShadedSelect
              className="min-w-[9rem]"
              value={uniqueUsersTimeRange}
              aria-label={t('analytics.selectTimeRange')}
              size="sm"
              onChange={(v) => setUniqueUsersTimeRange(v === 'all' ? 'all' : Number(v))}
              options={timeRangeOptions}
            />
            <ShadedSelect
              className="min-w-[8rem]"
              value={topXRoutes}
              size="sm"
              onChange={(v) => setTopXRoutes(v === 'all' ? 'all' : parseInt(v, 10))}
              options={topXOptions}
            />
          </>
        }
      >
        {uniqueUsersDateData.labels.length ? (
          <ChartPanel
            t={t}
            yLabel={t('analytics.count')}
            data={{
              labels: uniqueUsersDateData.labels,
              datasets: buildRouteDatasets({
                labels: uniqueUsersDateData.labels,
                byRoute: uniqueUsersByDayByRoute,
                topX: topXRoutes,
                totalLabel: t('analytics.uniqueUsers'),
                otherLabel: t('analytics.endpointOther')
              })
            }}
          />
        ) : (
          <p className="text-sm text-ink-muted">{t('analytics.emptyChart')}</p>
        )}
      </SectionCard>

      <SectionCard title={t('analytics.routeTotalRequests')} hint={t('analytics.routeTotalsHint')}>
        {sortedRouteTotals.length ? (
          <div className="overflow-x-auto rounded-md border border-edge">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-ink-muted">
                    {t('analytics.route')}
                  </th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-ink-muted">
                    {t('analytics.totalRequests')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedRouteTotals.map(({ route_path, route_method, total_requests }) => (
                  <tr key={`${route_method}:${route_path}`} className="border-t border-edge">
                    <td className="px-3 py-2 text-left font-mono text-xs text-ink break-all">
                      <span className="mr-2 rounded border border-edge bg-canvas px-1.5 py-0.5 text-[10px] font-sans font-semibold text-ink-muted">
                        {route_method}
                      </span>
                      {route_path}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-ink">
                      {Number(total_requests).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-ink-muted">{t('analytics.emptyChart')}</p>
        )}
      </SectionCard>

      <SectionCard
        title={t('analytics.enhancedAnalytics')}
        hint={t('analytics.enhancedHint')}
        actions={
          <button
            type="button"
            onClick={() => setShowEnhancedAnalytics((v) => !v)}
            className="rounded-md border border-edge-strong bg-surface px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {showEnhancedAnalytics ? t('analytics.hideDetails') : t('analytics.showDetails')}
          </button>
        }
      >
        {!showEnhancedAnalytics ? (
          <p className="text-sm text-ink-muted">{t('analytics.enhancedCollapsed')}</p>
        ) : enhancedAnalytics.length === 0 ? (
          <p className="text-sm text-ink-muted">{t('analytics.emptyChart')}</p>
        ) : (
          <div className="space-y-4">
            {enhancedAnalytics.map((routeData) => {
              const tallies = routeData.daily_tallies || [];
              const totalRequests = tallies.reduce((sum, tally) => sum + tally.total_requests, 0);
              const totalUniqueUsers = tallies.reduce((sum, tally) => sum + tally.unique_users, 0);
              const avgResponseTime =
                tallies.length > 0
                  ? tallies.reduce((sum, tally) => sum + tally.avg_response_time, 0) / tallies.length
                  : 0;
              const statusCodeCounts = {};
              tallies.forEach((tally) => {
                Object.entries(tally.status_codes || {}).forEach(([code, count]) => {
                  statusCodeCounts[code] = (statusCodeCounts[code] || 0) + count;
                });
              });

              return (
                <div
                  key={`${routeData.route_method}:${routeData.route_path}`}
                  className="rounded-lg border border-edge bg-canvas p-4"
                >
                  <h3 className="text-sm font-semibold text-ink break-all">
                    <span className="mr-2 rounded border border-edge bg-surface px-1.5 py-0.5 text-[10px] font-semibold text-ink-muted">
                      {routeData.route_method}
                    </span>
                    {routeData.route_path}
                  </h3>

                  <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
                    <StatCard
                      label={t('analytics.totalRequests')}
                      value={totalRequests.toLocaleString()}
                    />
                    <StatCard
                      label={t('analytics.uniqueUsers')}
                      value={totalUniqueUsers.toLocaleString()}
                    />
                    <StatCard
                      label={t('analytics.avgResponseTime')}
                      value={`${Math.round(avgResponseTime)}ms`}
                    />
                    <StatCard label={t('analytics.daysTracked')} value={tallies.length} />
                  </div>

                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <div>
                      <p className="mb-1.5 text-xs font-medium text-ink-muted">
                        {t('analytics.statusCodeDistribution')}
                      </p>
                      <div className="rounded-md border border-edge bg-surface px-3 py-2">
                        {Object.entries(statusCodeCounts)
                          .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
                          .map(([code, count]) => (
                            <div
                              key={code}
                              className="flex justify-between gap-3 border-t border-edge py-1.5 first:border-t-0 first:pt-0 last:pb-0"
                            >
                              <span className="font-mono text-xs text-ink">{code}</span>
                              <span className="tabular-nums text-xs font-semibold text-ink-muted">
                                {count.toLocaleString()}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>

                    <div>
                      <p className="mb-1.5 text-xs font-medium text-ink-muted">
                        {t('analytics.recentActivity')}
                      </p>
                      <div className="max-h-40 overflow-y-auto rounded-md border border-edge bg-surface px-3 py-2">
                        {[...tallies]
                          .sort((a, b) => b.date.localeCompare(a.date))
                          .slice(0, 5)
                          .map((tally) => (
                            <div
                              key={tally.date}
                              className="border-t border-edge py-2 first:border-t-0 first:pt-0 last:pb-0"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-sm font-medium text-ink">{tally.date}</span>
                                <span className="text-xs text-ink-muted">
                                  {tally.total_requests} {t('analytics.requests')}
                                </span>
                              </div>
                              <p className="mt-0.5 text-xs text-ink-subtle">
                                {tally.unique_users} {getUserText(tally.unique_users)} ·{' '}
                                {Math.round(tally.avg_response_time)}ms {t('analytics.avg')}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default PresentationAnalytics;
